// Exclude characters from the auction pool and pick which roles battles use, like Anime Draft's
// Filter / Exclude and role picker. In Cross Draft the exclude list is grouped by verse, so whole
// verses can be left in or out.
import { useMemo, useState } from 'react';
import { Ban, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { CROSS_DRAFT_VERSE, STAT_LABELS, getActiveRoles, getVersePool, getVerseRoles, type Character } from '@/data/characters';
import type { GameSettings } from '@/types/game';

const MIN_ROLES = 3;

interface DraftOptionsProps {
  settings: GameSettings;
  updateSettings: (patch: Partial<GameSettings>) => void;
  disabled?: boolean;
}

// How many characters are left to auction once exclusions are taken out.
export function usePoolSize(settings: GameSettings) {
  return useMemo(() => {
    const excluded = new Set(settings.excludedCharacters);
    const pool = getVersePool(settings.verse);
    return { total: pool.length, available: pool.filter((character) => !excluded.has(character.id)).length };
  }, [settings.verse, settings.excludedCharacters]);
}

export function DraftOptions({ settings, updateSettings, disabled = false }: DraftOptionsProps) {
  const [open, setOpen] = useState<'exclude' | 'roles' | null>(null);
  const { total, available } = usePoolSize(settings);
  const allRoles = getVerseRoles(settings.verse);
  const activeRoles = getActiveRoles(settings.verse, settings.rolesByVerse);
  const tooSmall = available < settings.auctionCount;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" disabled={disabled} onClick={() => setOpen('exclude')} className="h-auto flex-col gap-0.5 py-2">
          <span className="flex items-center gap-1.5 font-display text-sm font-bold"><Ban className="h-4 w-4" />Exclude Characters</span>
          <span className="text-[11px] text-muted-foreground">{total - available} excluded · {available} in pool</span>
        </Button>
        <Button variant="outline" disabled={disabled || allRoles.length === 0} onClick={() => setOpen('roles')} className="h-auto flex-col gap-0.5 py-2">
          <span className="flex items-center gap-1.5 font-display text-sm font-bold"><Shield className="h-4 w-4" />Roles</span>
          <span className="text-[11px] text-muted-foreground">{activeRoles.length} of {allRoles.length} roles</span>
        </Button>
      </div>
      {settings.verse === CROSS_DRAFT_VERSE && (
        <p className="text-center text-xs text-muted-foreground">
          Cross Draft mixes every verse. Characters keep their own verse's stats, so battles use the roles every verse shares.
        </p>
      )}
      {tooSmall && (
        <p className="text-center text-xs font-semibold text-destructive">
          Only {available} characters left in the pool. Include more, or lower the auction count to {available} or less.
        </p>
      )}

      <ExcludeDialog open={open === 'exclude'} onClose={() => setOpen(null)} settings={settings} updateSettings={updateSettings} />
      <RolesDialog open={open === 'roles'} onClose={() => setOpen(null)} settings={settings} updateSettings={updateSettings} />
    </div>
  );
}

function ExcludeDialog({ open, onClose, settings, updateSettings }: { open: boolean; onClose: () => void } & DraftOptionsProps) {
  const [search, setSearch] = useState('');
  const pool = useMemo(() => getVersePool(settings.verse), [settings.verse]);
  const excluded = useMemo(() => new Set(settings.excludedCharacters), [settings.excludedCharacters]);
  const query = search.trim().toLowerCase();
  const shown = pool.filter((character) => !query || character.name.toLowerCase().includes(query) || character.verse.toLowerCase().includes(query));

  // Cross Draft groups by verse; a single verse is one group.
  const groups = useMemo(() => {
    const byVerse = new Map<string, Character[]>();
    shown.forEach((character) => {
      const key = settings.verse === CROSS_DRAFT_VERSE ? character.verse : settings.verse;
      byVerse.set(key, [...(byVerse.get(key) ?? []), character]);
    });
    return [...byVerse.entries()];
  }, [shown, settings.verse]);

  const setExcluded = (ids: string[], exclude: boolean) => {
    const next = new Set(settings.excludedCharacters);
    ids.forEach((id) => (exclude ? next.add(id) : next.delete(id)));
    updateSettings({ excludedCharacters: [...next] });
  };

  const inPool = pool.filter((character) => !excluded.has(character.id)).length;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden p-0">
        <div className="flex max-h-[90vh] flex-col">
          <DialogHeader className="border-b border-border p-5 pb-4">
            <DialogTitle className="font-display text-xl font-black">Exclude Characters</DialogTitle>
            <DialogDescription>
              Tap a character to leave them out of the auctions{settings.verse === CROSS_DRAFT_VERSE ? ', or include and exclude whole verses' : ''}.
              {' '}{inPool} of {pool.length} in the pool.
            </DialogDescription>
            <div className="flex flex-wrap gap-2 pt-2">
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search characters or verses..." className="min-w-0 flex-1" />
              <Button variant="outline" onClick={() => setExcluded(shown.map((c) => c.id), true)}>Exclude shown</Button>
              <Button variant="outline" onClick={() => setExcluded(shown.map((c) => c.id), false)}>Include shown</Button>
            </div>
          </DialogHeader>
          <div className="flex-1 space-y-5 overflow-y-auto p-5">
            {groups.map(([verse, characters]) => {
              const excludedCount = characters.filter((c) => excluded.has(c.id)).length;
              return (
                <section key={verse}>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-display text-sm font-black uppercase tracking-wide text-foreground">
                      {verse} <span className="font-sans text-xs font-normal normal-case text-muted-foreground">· {characters.length - excludedCount} of {characters.length} in</span>
                    </h3>
                    {settings.verse === CROSS_DRAFT_VERSE && (
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setExcluded(characters.map((c) => c.id), false)}>Include verse</Button>
                        <Button size="sm" variant="outline" onClick={() => setExcluded(characters.map((c) => c.id), true)}>Exclude verse</Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
                    {characters.map((character) => {
                      const isOut = excluded.has(character.id);
                      return (
                        <button
                          key={character.id}
                          type="button"
                          onClick={() => setExcluded([character.id], !isOut)}
                          className={`relative overflow-hidden rounded-lg border text-left transition ${isOut ? 'border-destructive/60 opacity-40' : 'border-border hover:border-primary/60'}`}
                          style={{ aspectRatio: '3 / 4' }}
                          title={isOut ? `Include ${character.name}` : `Exclude ${character.name}`}
                        >
                          <img src={character.portrait} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" />
                          <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-1 pb-0.5 pt-3 text-[9px] font-semibold leading-tight text-white line-clamp-2">
                            {character.name}
                          </span>
                          {isOut && (
                            <span className="absolute inset-x-0 top-1/3 bg-destructive/90 py-0.5 text-center text-[9px] font-black uppercase tracking-wider text-white">Excluded</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>
              );
            })}
            {groups.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No characters match that search.</p>}
          </div>
          <div className="border-t border-border p-4">
            <Button className="w-full font-display font-bold" onClick={onClose}>Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RolesDialog({ open, onClose, settings, updateSettings }: { open: boolean; onClose: () => void } & DraftOptionsProps) {
  const allRoles = getVerseRoles(settings.verse);
  const active = getActiveRoles(settings.verse, settings.rolesByVerse);
  const minimum = Math.min(MIN_ROLES, allRoles.length);

  const setRoles = (roles: string[]) => {
    const next = { ...settings.rolesByVerse };
    if (roles.length >= allRoles.length) delete next[settings.verse];
    else next[settings.verse] = roles;
    updateSettings({ rolesByVerse: next });
  };

  const toggle = (role: string) => {
    if (active.includes(role)) {
      if (active.length <= minimum) return;
      setRoles(active.filter((r) => r !== role));
    } else {
      setRoles(allRoles.filter((r) => r === role || active.includes(r)));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black">Battle Roles</DialogTitle>
          <DialogDescription>
            Pick the roles battles are fought on for {settings.verse}. Keep at least {minimum}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {allRoles.map((role) => {
            const on = active.includes(role);
            return (
              <button
                key={role}
                type="button"
                onClick={() => toggle(role)}
                className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition ${on ? 'border-primary bg-primary/15 text-primary' : 'border-border bg-background/40 text-muted-foreground line-through'}`}
              >
                {STAT_LABELS[role]?.label ?? role}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => setRoles(allRoles)}>Use all roles</Button>
          <Button className="flex-1 font-display font-bold" onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
